/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Edit,
  Trash2,
  Search,
  Eye,
  Calendar,
  IndianRupee,
  Printer,
  Download,
  CreditCard
} from 'lucide-react';
import { generatePdfFromInvoiceLayout } from '../utils/pdfGenerator';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../utils/AuthContext';
import { listInvoices, getInvoiceItems, getInvoice, deleteInvoice as dbDeleteInvoice } from '../utils/db/invoices';
import { getProfile } from '../utils/db/profiles';
import InvoiceLayout from '../components/InvoiceLayout';
import NonGstInvoiceLayout from '../components/NonGstInvoiceLayout';

const ViewInvoices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [showPrintView, setShowPrintView] = useState(false);
  const [franchiseProfile, setFranchiseProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('gst'); // 'gst' or 'non-gst'

  const printComponentRef = React.useRef();
  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
    documentTitle: selectedInvoice ? `Invoice_${selectedInvoice.invoice_no}` : 'Invoice'
  });
  const downloadRef = React.useRef();
  const [downloadingId, setDownloadingId] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(false);

  useEffect(() => {
    if (user) {
      loadInvoices();
      loadFranchiseProfile();
    }
  }, [user]);

  const loadFranchiseProfile = async () => {
    try {
      const profile = await getProfile(user.id);
      if (profile) {
        // Transform database fields to match the expected format
        const transformedProfile = {
          franchiseName: profile.franchise_name || 'TATYS',
          address: profile.address || 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
          mobileNo: profile.mobile_no || '8015695652',
          email: profile.email || 'thirumazhisai.dist@gmail.com',
          gstNo: profile.gst_no || '27CIOPP1874N1Z',
          panNo: profile.pan_no || 'CIOPP1874N',
          bankName: profile.bank_name || 'Canara Bank',
          accountNo: profile.account_no || '102000820462',
          ifsc: profile.ifsc || 'CNRB0003072',
          branchName: profile.branch_name || 'THIRUMAZHISAI',
          hsn_sac: profile.hsn_sac || null,
          invoicePrefix: profile.invoice_prefix || 'INV',
          logoUrl: profile.logo_url || null,
          terms: profile.terms ? profile.terms.split('\n') : [
            'Payment should be made by cheque in favour of TATYS',
            'Any queries must be forwarded within 15 days of receipt',
            '18% interest will be charged on overdue bills'
          ]
        };
        setFranchiseProfile(transformedProfile);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback
      setFranchiseProfile({
        franchiseName: 'TATYS',
        address: 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
        mobileNo: '8015695652',
        email: 'thirumazhisai.dist@gmail.com',
        gstNo: '27CIOPP1874N1Z',
        panNo: 'CIOPP1874N',
        bankName: 'Canara Bank',
        accountNo: '102000820462',
        ifsc: 'CNRB0003072',
        branchName: 'THIRUMAZHISAI',
        terms: [
          'Payment should be made by cheque in favour of TATYS',
          'Any queries must be forwarded within 15 days of receipt',
          '18% interest will be charged on overdue bills'
        ]
      });
    }
  };

  const loadInvoices = async () => {
    try {
      const rows = await listInvoices(user.id);
      setInvoices(rows);
    } catch (e) {
      toast.error('Failed to load invoices');
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await dbDeleteInvoice(user.id, invoiceId);
      await loadInvoices();
      toast.success('Invoice deleted successfully!');
    } catch (e) {
      toast.error('Failed to delete invoice');
    }
  };

  const viewInvoice = async (invoice) => {
    try {
      setIsLoading(true);
      const [fullInvoice, items] = await Promise.all([
        getInvoice(user.id, invoice.id),
        getInvoiceItems(user.id, invoice.id)
      ]);
      setSelectedInvoice(fullInvoice);
      setInvoiceItems(items);
      setShowPrintView(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      if (downloadingId) return;
      setDownloadingId(invoice.id);
      setPendingDownload(true);
      const [fullInvoice, items] = await Promise.all([
        getInvoice(user.id, invoice.id),
        getInvoiceItems(user.id, invoice.id)
      ]);
      setInvoiceItems(items || []);
      setSelectedInvoice(fullInvoice);

      // wait for offscreen layout to render
      setTimeout(async () => {
        try {
          await generatePdfFromLayout();
        } catch (err) {
          console.error(err);
          toast.error('Failed to generate PDF');
        } finally {
          setSelectedInvoice(null);
          setInvoiceItems([]);
          setPendingDownload(false);
          setDownloadingId(null);
        }
      }, 400);
    } catch (e) {
      console.error(e);
      toast.error('Failed to prepare invoice for download');
      setPendingDownload(false);
      setDownloadingId(null);
    }
  };

  const generatePdfFromLayout = async () => {
    const container = downloadRef.current;
    if (!container) throw new Error('Layout not ready');
    // Check if current selected invoice is Non-GST to prepend correct prefix in filename if needed
    const prefix = selectedInvoice?.invoice_no?.startsWith('NG-') ? 'NonGst_' : '';
    await generatePdfFromInvoiceLayout(container, `${prefix}Invoice_${selectedInvoice?.invoice_no || 'Invoice'}`);
  };

  // Calculate days between two dates (inclusive)
  function calculateDays(fromDate, toDate) {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  }

  // Calculate total weight from items
  function calculateTotalWeight(items) {
    if (!items || !items.length) return 0;
    const totalWeight = items.reduce((sum, item) => {
      return sum + (parseFloat(item.weight) || 0);
    }, 0);
    return totalWeight.toFixed(2);
  }

  // Format booking items for the invoice table
  function formatItems(items) {
    if (!items || !items.length) return [];
    return items.map(item => ({
      date: item.date,
      cnote: item.awb_no,
      receiver: item.receiver_name,
      destination: item.receiver_address,
      mode: item.mode,
      weight: item.weight,
      qty: item.quantity,
      type: item.type,
      amount: Number(item.amount || 0).toFixed(2),
      other: Number(item.other_charge || 0).toFixed(2),
      total: (Number(item.amount || 0) + Number(item.other_charge || 0)).toFixed(2)
    }));
  };

  // Helper to check if invoice is GST or Non-GST
  const isNonGst = (invoice) => {
    return invoice?.invoice_no?.toString().startsWith('NG-');
  };

  // Build invoice payload for InvoiceLayout (shared between print modal and offscreen PDF)
  const buildInvoicePayload = (inv, items) => {
    const nonGst = isNonGst(inv);
    const subtotal = Number(inv.subtotal || 0);
    const totalBeforeGST = Number(inv.total_before_gst || subtotal);

    if (nonGst) {
      // NON-GST Payload Construction
      const gstType = inv.gst_type || 'IGST';
      return {
        number: `${inv.invoice_no}`, // Already has NG- prefix
        date: inv.invoice_date,
        fromDate: inv.from_date,
        toDate: inv.to_date,
        customer: {
          name: inv.customers?.customer_name || '',
          address: inv.customers?.address || '',
          gst: inv.customers?.gst_no || ''
        },
        days: calculateDays(inv.from_date, inv.to_date),
        consignment: (items || []).length || 0,
        weight: calculateTotalWeight(items),
        amount: (() => {
          try {
            const sum = (items || []).reduce((s, it) => s + Number(it.amount || 0), 0);
            return Number(sum).toFixed(2);
          } catch { return '0.00'; }
        })(),
        items: formatItems(items),
        subtotal: Number(inv.subtotal || 0).toFixed(2),
        fuelPercent: Number(inv.fuel_percent || 0).toFixed(2),
        fuelCharge: Number(inv.fuel_charge || 0).toFixed(2),
        royaltyPercent: Number(inv.royalty_percent || 0).toFixed(2),
        royaltyCharge: Number(inv.royalty_charge || 0).toFixed(2),
        docketCharge: Number(inv.docket_charge || 0).toFixed(2),
        odaCharge: Number(inv.oda_charge || 0).toFixed(2),
        otherCharge: Number(inv.other_charge || 0).toFixed(2),
        gstType,
        cgstPercent: '0',
        cgstAmount: '0.00',
        sgstPercent: '0',
        sgstAmount: '0.00',
        igstPercent: '0',
        igstAmount: '0.00',
        igst: '0.00',
        total: Number(inv.grand_total || (totalBeforeGST) || 0).toFixed(2)
      };
    } else {
      // GST Payload Construction
      const userPercent = Number(inv.gst_percent || 0);
      let gstType = inv.gst_type || 'IGST';
      if (!inv.gst_type) {
        const isSameState = inv.customers?.customer_state === 'same';
        gstType = isSameState ? 'CGST+SGST' : 'IGST';
      }

      let cgstPercent = 0, sgstPercent = 0, igstPercent = 0;
      let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

      if (gstType === 'CGST+SGST') {
        cgstPercent = Number(inv.cgst_percent != null ? inv.cgst_percent : userPercent / 2);
        sgstPercent = Number(inv.sgst_percent != null ? inv.sgst_percent : userPercent / 2);
        cgstAmount = Number(inv.cgst_amount != null ? inv.cgst_amount : (totalBeforeGST * cgstPercent) / 100);
        sgstAmount = Number(inv.sgst_amount != null ? inv.sgst_amount : (totalBeforeGST * sgstPercent) / 100);
      } else {
        igstPercent = Number(inv.igst_percent != null ? inv.igst_percent : userPercent);
        igstAmount = Number(inv.igst_amount != null ? inv.igst_amount : (totalBeforeGST * igstPercent) / 100);
      }

      const computedGst = cgstAmount + sgstAmount + igstAmount;
      const gstAmount = Number(inv.gst_amount != null ? inv.gst_amount : computedGst);

      return {
        number: inv.invoice_no,
        date: inv.invoice_date,
        fromDate: inv.from_date,
        toDate: inv.to_date,
        customer: {
          name: inv.customers?.customer_name || '',
          address: inv.customers?.address || '',
          gst: inv.customers?.gst_no || ''
        },
        days: calculateDays(inv.from_date, inv.to_date),
        consignment: (items || []).length || 0,
        weight: calculateTotalWeight(items),
        amount: (() => {
          try {
            const sum = (items || []).reduce((s, it) => s + Number(it.amount || 0), 0);
            return Number(sum).toFixed(2);
          } catch { return '0.00'; }
        })(),
        items: formatItems(items),
        subtotal: Number(inv.subtotal || 0).toFixed(2),
        fuelPercent: Number(inv.fuel_percent || 0).toFixed(2),
        fuelCharge: Number(inv.fuel_charge || 0).toFixed(2),
        royaltyPercent: Number(inv.royalty_percent || 0).toFixed(2),
        royaltyCharge: Number(inv.royalty_charge || 0).toFixed(2),
        docketCharge: Number(inv.docket_charge || 0).toFixed(2),
        odaCharge: Number(inv.oda_charge || 0).toFixed(2),
        otherCharge: Number(inv.other_charge || 0).toFixed(2),
        gstType,
        cgstPercent: Number(cgstPercent).toFixed(0),
        cgstAmount: Number(cgstAmount).toFixed(2),
        sgstPercent: Number(sgstPercent).toFixed(0),
        sgstAmount: Number(sgstAmount).toFixed(2),
        igstPercent: Number(igstPercent || (gstType === 'IGST' ? userPercent : 0)).toFixed(0),
        igstAmount: Number(igstAmount).toFixed(2),
        igst: Number(gstAmount).toFixed(2),
        total: Number(inv.grand_total || (totalBeforeGST + gstAmount) || 0).toFixed(2)
      };
    }
  };

  // Filter invoices based on Active Tab
  const getTabFilteredInvoices = () => {
    return invoices.filter(invoice => {
      const isNG = invoice.invoice_no?.startsWith('NG-');
      return activeTab === 'gst' ? !isNG : isNG;
    });
  };

  // Filter the above list based on Search Term
  const filteredInvoices = getTabFilteredInvoices().filter(invoice =>
    invoice.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customers?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(invoice.invoice_date || '').includes(searchTerm)
  );

  // Calculate total revenue from current filtered view
  const totalRevenue = filteredInvoices.reduce((sum, invoice) => {
    return sum + Number(invoice.grand_total || 0);
  }, 0);

  // Get stats for the top cards (using Tab filtered list, NOT search filtered, or maybe search filtered? usually general stats for the tab)
  // Let's use the tab-list (without search) for the top summary cards to show "Total" for that category.
  const currentTabInvoices = getTabFilteredInvoices();
  const summaryTotalRevenue = currentTabInvoices.reduce((sum, invoice) => sum + Number(invoice.grand_total || 0), 0);
  const currentMonthCount = currentTabInvoices.filter(invoice => {
    const invoiceDate = new Date(invoice.invoice_date);
    const currentMonth = new Date();
    return invoiceDate.getMonth() === currentMonth.getMonth() &&
      invoiceDate.getFullYear() === currentMonth.getFullYear();
  }).length;


  // Choose the Layout Component based on selected invoice type
  const SelectedLayoutComponent = selectedInvoice && isNonGst(selectedInvoice) ? NonGstInvoiceLayout : InvoiceLayout;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View Invoices</h1>
          <p className="text-gray-600">Overview of generated invoices</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('gst')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'gst'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            GST Invoices
          </button>
          <button
            onClick={() => setActiveTab('non-gst')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'non-gst'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            Non-GST Invoices
          </button>
        </nav>
      </div>

      {/* Summary Cards */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {activeTab === 'gst' ? 'GST Invoice Summary' : 'Non-GST Invoice Summary'}
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-primary-50 rounded-lg p-4">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-primary-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-primary-900">
                    {activeTab === 'gst' ? 'Total Invoices' : 'Total Non-GST'}
                  </p>
                  <p className="text-2xl font-bold text-primary-600">{currentTabInvoices.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <IndianRupee className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-900">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{summaryTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
            {/* Third card mostly relevant for GST/General stats, but can show Month stats for Non-GST too */}
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-900">This Month</p>
                  <p className="text-2xl font-bold text-purple-600">{currentMonthCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by number, customer, or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {activeTab === 'gst' ? 'GST List' : 'Non-GST List'} ({filteredInvoices.length})
          </h3>
          <div className="flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-md">
            <IndianRupee className="h-5 w-5 mr-2" />
            <div>
              <span className="text-sm font-medium">Revenue (View):</span>
              <span className="ml-2 text-lg font-bold">₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.length > 0 ? (
                filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {activeTab === 'gst' ? `#${invoice.invoice_no}` : invoice.invoice_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.customers?.customer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.invoice_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.from_date} - {invoice.to_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{Number(invoice.grand_total || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => viewInvoice(invoice)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Invoice"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            // Route to appropriate generator
                            const route = activeTab === 'gst' ? '/invoice-generator' : '/invoice-generator-non-gst';
                            navigate(`${route}?edit=${invoice.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit Invoice"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadInvoice(invoice)}
                          disabled={downloadingId === invoice.id}
                          className="text-green-600 hover:text-green-900"
                          title="Download Invoice"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {/* <button
                          onClick={() => deleteInvoice(invoice.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Invoice"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    {searchTerm ? `No ${activeTab === 'gst' ? 'GST' : 'Non-GST'} invoices found matching your search.` : `No ${activeTab === 'gst' ? 'GST' : 'Non-GST'} invoices generated yet.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Print View Modal */}
      {showPrintView && selectedInvoice && SelectedLayoutComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {activeTab === 'gst' ? '' : 'Non-GST '}Invoice {isNonGst(selectedInvoice) ? selectedInvoice.invoice_no : `#${selectedInvoice.invoice_no}`}
                </h2>
                <button
                  onClick={() => setShowPrintView(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XIcon />
                </button>
              </div>
              <div className="space-y-4">
                <div ref={printComponentRef}>
                  <SelectedLayoutComponent
                    invoice={buildInvoicePayload(selectedInvoice, invoiceItems)}
                    profile={franchiseProfile}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page footer */}
      <footer className="mt-8 border-t border-gray-200">
        <div className="py-4 text-center text-xs text-gray-500">
          Proprietary Billing Infrastructure
        </div>
      </footer>

      {/* Offscreen layout for PDF generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, height: 0, overflow: 'hidden' }}>
        {selectedInvoice && SelectedLayoutComponent && (
          <div ref={downloadRef}>
            <SelectedLayoutComponent
              invoice={buildInvoicePayload(selectedInvoice, invoiceItems)}
              profile={franchiseProfile}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Simple helper component for the X icon since it was not imported
const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default ViewInvoices;